import React from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ServiceLayout } from '@/components/ServiceLayout';
import {
  Plus, ClipboardList, Upload as UploadIcon, FileBarChart2,
  Wrench, AlertCircle, Calendar, ArrowUpRight, Clock,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { DateRangePresets, computePresetRange } from '@/components/Service/DateRangePresets';
import { SLADot } from '@/components/Service/SLABadge';
import { DashboardBreakdownTabs } from '@/components/Service/DashboardBreakdownTabs';
import { useServiceMasters } from '@/hooks/useServiceMasters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#0EA5E9', '#6366F1', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4', '#F43F5E'];

// Parse "DD-MM-YYYY" or "YYYY-MM-DD" to Date
const parseDate = (s) => {
  if (!s) return null;
  const parts = String(s).split(/[-/]/);
  if (parts.length !== 3) return null;
  let d, m, y;
  if (parts[0].length === 4) { y = parts[0]; m = parts[1]; d = parts[2]; }
  else { d = parts[0]; m = parts[1]; y = parts[2]; if (y.length === 2) y = '20' + y; }
  const dt = new Date(`${y}-${m}-${d}T00:00:00`);
  return isNaN(dt.getTime()) ? null : dt;
};

export default function ServiceDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [jobs, setJobs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const masters = useServiceMasters();

  // iter-120: default date range is Month-to-Date (vehicle_in_date)
  const [activePreset, setActivePreset] = React.useState('mtd');
  const [dateFrom, setDateFrom] = React.useState(() => computePresetRange('mtd')[0]);
  const [dateTo, setDateTo] = React.useState(() => computePresetRange('mtd')[1]);
  const [sortDir, setSortDir] = React.useState('desc');

  React.useEffect(() => { fetchJobs(); }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/service/jobs?limit=5000`);
      setJobs(data || []);
    } catch {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter by date range
  const filteredJobs = React.useMemo(() => {
    const df = parseDate(dateFrom);
    const dt = parseDate(dateTo);
    if (!df && !dt) return jobs;
    return jobs.filter((j) => {
      const jd = parseDate(j.vehicle_in_date);
      if (!jd) return false;
      if (df && jd < df) return false;
      if (dt && jd > dt) return false;
      return true;
    });
  }, [jobs, dateFrom, dateTo]);

  const stats = React.useMemo(() => {
    const today = new Date();
    const todayStr = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;
    return {
      total: filteredJobs.length,
      todayJobs: filteredJobs.filter((j) => j.vehicle_in_date === todayStr).length,
      overdue: filteredJobs.filter((j) => j.sla_status === 'overdue').length,
      atRisk: filteredJobs.filter((j) => j.sla_status === 'at_risk').length,
    };
  }, [filteredJobs]);

  // Master-driven Status metric tiles — one per status master.
  // Counts are case-insensitive, exact match against `doc_type`.
  const statusMetrics = React.useMemo(() => {
    const list = masters.status || [];
    const norm = (s) => String(s || '').trim().toLowerCase();
    const counts = new Map(list.map((s) => [norm(s), 0]));
    filteredJobs.forEach((j) => {
      const k = norm(j.doc_type);
      if (counts.has(k)) counts.set(k, counts.get(k) + 1);
    });
    return list.map((label) => ({ label, value: counts.get(norm(label)) || 0 }));
  }, [filteredJobs, masters.status]);

  // Pick a stable accent per status name (deterministic hash → palette index)
  const statusAccent = React.useCallback((label) => {
    const palette = ['indigo', 'violet', 'amber', 'emerald', 'rose', 'sky', 'fuchsia', 'teal'];
    let h = 0;
    for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) & 0xffff;
    return palette[h % palette.length];
  }, []);

  const categoryData = React.useMemo(() => {
    const c = {};
    filteredJobs.forEach((j) => { const k = j.job_category || 'Uncategorized'; c[k] = (c[k] || 0) + 1; });
    return Object.entries(c).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 6);
  }, [filteredJobs]);

  const technicianData = React.useMemo(() => {
    const c = {};
    filteredJobs.forEach((j) => { const k = j.technician || 'Unassigned'; c[k] = (c[k] || 0) + 1; });
    return Object.entries(c).map(([name, value]) => ({ name: name.length > 14 ? name.slice(0, 14) + '…' : name, value })).sort((a, b) => b.value - a.value).slice(0, 8);
  }, [filteredJobs]);

  const recent = React.useMemo(() => {
    const arr = [...filteredJobs];
    arr.sort((a, b) => (sortDir === 'asc' ? (a.job_no_int || 0) - (b.job_no_int || 0) : (b.job_no_int || 0) - (a.job_no_int || 0)));
    return arr.slice(0, 8);
  }, [filteredJobs, sortDir]);

  return (
    <ServiceLayout user={user} onLogout={onLogout} title="Service Dashboard" subtitle={`${filteredJobs.length} jobs · ${dateFrom} → ${dateTo}`}>
      <div className="space-y-6" data-testid="service-dashboard">
        {/* Date Range filter */}
        <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/50 shadow-[0_8px_30px_rgba(15,23,42,0.04)] p-4">
          <DateRangePresets
            activePreset={activePreset} setActivePreset={setActivePreset}
            dateFrom={dateFrom} setDateFrom={setDateFrom}
            dateTo={dateTo} setDateTo={setDateTo}
            sortDir={sortDir} setSortDir={setSortDir}
          />
        </div>

        {/* Hero metrics — Total Jobs (retained) + master-driven Status tiles + SLA tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="service-status-metrics-grid">
          <Metric
            label="Total Jobs"
            value={stats.total}
            accent="indigo"
            Icon={Wrench}
            clickable
            onClick={() => navigate(`/service/jobs?date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}`)}
            testId="total-jobs-tile"
          />
          {statusMetrics.map(({ label, value }) => (
            <Metric
              key={label}
              label={label}
              value={value}
              accent={statusAccent(label)}
              Icon={Wrench}
              clickable
              onClick={() => navigate(
                `/service/jobs?doc_type=${encodeURIComponent(label)}&date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}`
              )}
              testId={`status-tile-${label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/^-+|-+$/g, '')}`}
            />
          ))}
          <Metric label="Today's Jobs" value={stats.todayJobs} accent="rose" Icon={Calendar} clickable onClick={() => navigate('/service/jobs?date_today=1')} testId="today-jobs-tile" />
          <Metric label="Overdue" value={stats.overdue} accent="rose" Icon={AlertCircle} clickable onClick={() => navigate('/service/jobs?sla=overdue')} testId="overdue-tile" />
          <Metric label="At Risk" value={stats.atRisk} accent="amber" Icon={Clock} clickable onClick={() => navigate('/service/jobs?sla=at_risk')} testId="at-risk-tile" />
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <ActionCard title="New Job Card" Icon={Plus} primary onClick={() => navigate('/service/jobs/new')} testId="qa-new-job" />
          <ActionCard title="All Job Cards" Icon={ClipboardList} onClick={() => navigate('/service/jobs')} testId="qa-all-jobs" />
          <ActionCard title="Upload Data" Icon={UploadIcon} onClick={() => navigate('/service/upload')} testId="qa-upload" />
          <ActionCard title="Reports" Icon={FileBarChart2} onClick={() => navigate('/service/reports/vehicle-inwards')} testId="qa-reports" />
        </div>

        {/* Breakdown tabs — Service Head / Job Type / Supervisor / Technician */}
        <DashboardBreakdownTabs jobs={filteredJobs} dateFrom={dateFrom} dateTo={dateTo} />

        {/* Charts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/50 shadow-[0_8px_30px_rgba(15,23,42,0.04)] p-5">
            <h3 className="text-[14px] font-semibold text-slate-800 mb-1">Job Categories</h3>
            <p className="text-[12px] text-slate-500 mb-3">Distribution by category</p>
            {categoryData.length ? (
              <div style={{ height: 280 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={categoryData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={92} paddingAngle={2}>
                      {categoryData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : <div className="h-64 flex items-center justify-center text-slate-400 text-[13px]">No data for selected range</div>}
          </div>

          <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/50 shadow-[0_8px_30px_rgba(15,23,42,0.04)] p-5">
            <h3 className="text-[14px] font-semibold text-slate-800 mb-1">Top Technicians</h3>
            <p className="text-[12px] text-slate-500 mb-3">Workload by technician</p>
            {technicianData.length ? (
              <div style={{ height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={technicianData} layout="vertical" margin={{ left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#475569' }} />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11, fill: '#475569' }} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
                    <Bar dataKey="value" fill="url(#barGrad2)" radius={[0, 6, 6, 0]} />
                    <defs>
                      <linearGradient id="barGrad2" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#0EA5E9" />
                        <stop offset="100%" stopColor="#6366F1" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : <div className="h-64 flex items-center justify-center text-slate-400 text-[13px]">No data for selected range</div>}
          </div>
        </div>

        {/* Recent jobs */}
        <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/50 shadow-[0_8px_30px_rgba(15,23,42,0.04)] overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <h3 className="text-[14px] font-semibold text-slate-800">Recent Jobs</h3>
            <button onClick={() => navigate('/service/jobs')} className="text-[12.5px] text-sky-600 hover:text-sky-800 font-medium inline-flex items-center gap-1">
              View all <ArrowUpRight size={13} />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left bg-sky-600 text-white">
                  <th className="p-3 font-semibold">Job No</th>
                  <th className="p-3 font-semibold">Party</th>
                  <th className="p-3 font-semibold">Reg No</th>
                  <th className="p-3 font-semibold">Service Head</th>
                  <th className="p-3 font-semibold">Technician</th>
                  <th className="p-3 font-semibold">In</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="p-8 text-center text-slate-400 text-[13px]">Loading…</td></tr>
                ) : recent.length === 0 ? (
                  <tr><td colSpan={6} className="p-8 text-center text-slate-400 text-[13px]">
                    No jobs in selected range. <button onClick={() => navigate('/service/upload')} className="text-sky-600 hover:underline font-medium">Upload data</button> or <button onClick={() => navigate('/service/jobs/new')} className="text-sky-600 hover:underline font-medium">create one</button>.
                  </td></tr>
                ) : recent.map((j, idx) => (
                  <tr key={j.id} className={`border-b border-slate-100 hover:bg-sky-100/40 ${idx % 2 === 0 ? 'bg-white' : 'bg-sky-50/60'}`}>
                    <td className="p-3 font-semibold text-sky-700">
                      <button onClick={() => navigate('/service/jobs')} className="hover:underline">{j.job_no}</button>
                    </td>
                    <td className="p-3 text-slate-800">{j.party_name || '—'}</td>
                    <td className="p-3 text-slate-600 font-mono">{j.reg_no || '—'}</td>
                    <td className="p-3 text-slate-600">{j.service_head || '—'}</td>
                    <td className="p-3 text-slate-600">{j.technician || '—'}</td>
                    <td className="p-3 text-slate-500">{j.vehicle_in_date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ServiceLayout>
  );
}

const accentMap = {
  indigo: 'from-sky-500 to-indigo-500',
  violet: 'from-violet-500 to-fuchsia-500',
  rose: 'from-rose-500 to-pink-500',
  amber: 'from-amber-500 to-orange-500',
  emerald: 'from-emerald-500 to-teal-500',
  sky: 'from-sky-400 to-cyan-500',
  fuchsia: 'from-fuchsia-500 to-pink-500',
  teal: 'from-teal-500 to-emerald-500',
};

const Metric = ({ label, value, accent = 'indigo', Icon, clickable, onClick, testId }) => {
  const Wrapper = clickable ? 'button' : 'div';
  return (
    <Wrapper
      type={clickable ? 'button' : undefined}
      onClick={clickable ? onClick : undefined}
      data-testid={testId}
      className={`relative rounded-2xl bg-white/75 backdrop-blur border border-white/50 p-4 overflow-hidden shadow-[0_8px_30px_rgba(15,23,42,0.04)] text-left ${clickable ? 'hover:shadow-md hover:border-sky-200 transition-all cursor-pointer' : ''}`}
    >
      <div className={`absolute -top-10 -right-10 w-28 h-28 rounded-full opacity-15 blur-2xl bg-gradient-to-br ${accentMap[accent]}`} />
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={14} className="text-slate-400" />}
        <p className="text-[11.5px] uppercase tracking-wider text-slate-500 font-medium">{label}</p>
      </div>
      <p className="text-[28px] font-semibold tracking-tight text-slate-900 leading-none">{value}</p>
    </Wrapper>
  );
};

const ActionCard = ({ title, Icon, onClick, primary, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`group relative rounded-2xl p-4 border transition-all flex items-center gap-3 ${
      primary
        ? 'text-white shadow-md shadow-sky-500/20 hover:shadow-lg border-transparent'
        : 'bg-white/70 backdrop-blur border-white/50 text-slate-700 hover:bg-white hover:border-sky-200'
    }`}
    style={primary ? { background: 'linear-gradient(135deg, #0EA5E9 0%, #4F46E5 60%, #DB2777 100%)' } : {}}
  >
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${primary ? 'bg-white/20' : 'bg-sky-50 text-sky-600'}`}>
      <Icon size={18} />
    </div>
    <span className="text-[14px] font-medium text-left">{title}</span>
    <ArrowUpRight size={16} className={`ml-auto transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${primary ? 'text-white/80' : 'text-slate-400'}`} />
  </button>
);
